import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowRight,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Layers,
  Loader2,
  Maximize2,
  Minus,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { NumericInput } from "@/components/ui/numeric-input";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { useConfigLookup } from "@/hooks/useConfigLookup";
import { trpcClient } from "@/lib/trpc";
import { batchAssignImpositionMutate } from "@/lib/trpc-helpers";
import { getFullUrl, uploadFile } from "@/lib/uploadFile";
import { cn, formatNumber } from "@/lib/utils";
import {
  calculateRowPlannedQty,
  checkImpositionCompatibility,
  extractMaterialSignature,
  extractRoutingSignature,
  generateSuggestedLayoutCode,
  resolveOrderCode,
  resolveOrderKey,
  resolveRemainingPrepressQty,
  suggestSheetsPerUp,
  validateBulkImpositionSelection,
} from "./prepress-imposition.helper";

export interface ImpositionRowState {
  orderDetailId: string;
  orderId: string;
  orderCode: string;
  customerName: string;
  productId: string | null;
  productCode: string;
  productName: string;
  dimensionsLabel: string;
  orderQty: number;
  targetQty: number;
  ups: number;
  plannedQty: number;
  isManualPlannedQty: boolean;
  rawDetail: any;
}

export interface PrintLayoutFormSuccessResult {
  id: string;
  code: string;
  label: string;
  record: any;
  createPo?: boolean;
}

export interface PrintLayoutFormProps {
  mode?: "create" | "edit";
  layoutId?: string | null;
  initialDetails?: any[];
  orderId?: string | null;
  orderCode?: string | null;
  isDrawerMode?: boolean;
  focusField?: "code" | "sheetSize" | "ups" | "sheets" | null;
  onSuccess?: (result: PrintLayoutFormSuccessResult) => void | Promise<void>;
  onCancel?: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
  /**
   * Nhúng trong trang tạo lệnh: không tự chuyển trang khi "Lưu & Tạo Lệnh SX" — trang cha nhận onSuccess (createPo) và tự sang bước lập lệnh.
   * Mặc định true (các màn bình file độc lập giữ hành vi cũ).
   */
  navigateOnCreatePo?: boolean;
  /** Nhãn nút lưu & lập lệnh (mặc định "Lưu & Tạo Lệnh SX") */
  createPoLabel?: string;
}

function buildDimensionsLabel(detail: any): string {
  const flatL = detail?.product?.flatLength ?? detail?.flatLength;
  const flatW = detail?.product?.flatWidth ?? detail?.flatWidth;
  if (flatL && flatW) {
    return `Trải: ${flatW} × ${flatL} mm`;
  }
  const L = detail?.product?.length ?? detail?.length;
  const W = detail?.product?.width ?? detail?.width;
  const H = detail?.product?.height ?? detail?.height;
  if (L && W && H) {
    return `${L} × ${W} × ${H} mm`;
  }
  if (L && W) {
    return `${L} × ${W} mm`;
  }
  if (detail?.product?.finishedSize) {
    return String(detail.product.finishedSize);
  }
  return "";
}

function mapDetailToRow(
  detail: any,
  opts?: {
    overrideUps?: number;
    overridePlannedQty?: number;
    sheetsPerUp?: number;
    excludeLayoutId?: string | null;
    fallbackOrderCode?: string | null;
  },
): ImpositionRowState {
  const orderQty = Math.max(1, Number(detail?.quantity || 1));
  const remaining = resolveRemainingPrepressQty(detail, opts?.excludeLayoutId);
  const targetQty = remaining > 0 ? remaining : orderQty;
  const defaultUps = Math.max(
    1,
    Number(
      opts?.overrideUps ??
        (remaining === 0 && detail?.prepressUps ? detail.prepressUps : detail?.product?.outs) ??
        1,
    ),
  );
  const effectiveSheets = Math.max(
    1,
    Number(opts?.sheetsPerUp ?? Math.ceil(targetQty / defaultUps)),
  );
  const autoPlanned = calculateRowPlannedQty(defaultUps, effectiveSheets);
  const plannedQty =
    opts?.overridePlannedQty && opts.overridePlannedQty > 0
      ? Math.round(opts.overridePlannedQty)
      : autoPlanned;

  return {
    orderDetailId: String(detail.id),
    orderId: resolveOrderKey(detail),
    orderCode: resolveOrderCode(detail) || String(opts?.fallbackOrderCode || ""),
    customerName: String(
      detail?.order?.customer?.name ||
        detail?.customerName ||
        detail?.order?.customer?.shortName ||
        "—",
    ),
    productId: detail?.productId || detail?.product?.id || null,
    productCode: String(detail?.productCode || detail?.product?.productCode || ""),
    productName: String(detail?.productName || detail?.product?.productName || "Sản phẩm"),
    dimensionsLabel: buildDimensionsLabel(detail),
    orderQty,
    targetQty,
    ups: defaultUps,
    plannedQty,
    isManualPlannedQty: Boolean(
      opts?.overridePlannedQty && opts.overridePlannedQty !== autoPlanned,
    ),
    rawDetail: detail,
  };
}

export function PrintLayoutForm({
  mode = "create",
  layoutId = null,
  initialDetails = [],
  orderId: initialOrderId = null,
  orderCode: initialOrderCode = null,
  isDrawerMode = false,
  focusField = null,
  onSuccess,
  onCancel,
  onSubmittingChange,
  navigateOnCreatePo = true,
  createPoLabel = "Lưu & Tạo Lệnh SX",
}: PrintLayoutFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Determine whether we are editing an existing PrintLayout
  const inferredEditLayoutId = useMemo(() => {
    if (layoutId) return layoutId;
    if (mode === "edit" && initialDetails.length === 1 && initialDetails[0]?.printLayoutId) {
      return String(initialDetails[0].printLayoutId);
    }
    if (initialDetails.length === 1) {
      const first = initialDetails[0];
      const rem = resolveRemainingPrepressQty(first);
      if (rem === 0 && first?.printLayoutId) {
        return String(first.printLayoutId);
      }
    }
    return null;
  }, [layoutId, mode, initialDetails]);

  const { data: existingLayout, isLoading: isLoadingExisting } = useQuery({
    queryKey: ["print-layout-detail", inferredEditLayoutId],
    queryFn: () => trpcClient.prepress.getPrintLayout.query({ id: inferredEditLayoutId! }),
    enabled: Boolean(inferredEditLayoutId),
  });

  // Dynamic sheet size presets from DB config (`prepress_sheet_sizes`) — ZERO hardcoded arrays
  const { options: sheetSizePresets } = useConfigLookup("prepress_sheet_sizes", {
    valueField: "name",
  });

  // Master General Info state
  const [prepressCode, setPrepressCode] = useState("");
  const [sheetSize, setSheetSize] = useState("");
  const [sheetsPerUp, setSheetsPerUp] = useState<number>(1);
  const [isManualSheetsPerUp, setIsManualSheetsPerUp] = useState(false);
  const [proofUrl, setProofUrl] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [machineId, setMachineId] = useState<string | null>(null);
  const [dieCutId, setDieCutId] = useState<string | null>(null);
  const [showAdvancedTech, setShowAdvancedTech] = useState(false);

  // Detail Rows state
  const [rows, setRows] = useState<ImpositionRowState[]>([]);

  // Picker search state
  const [pickerSearch, setPickerSearch] = useState("");
  const [showPickerDropdown, setShowPickerDropdown] = useState(false);

  // Upload & Submit state
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingCreatePo, setPendingCreatePo] = useState(false);

  // Interaction validation state (`validate-input-after-interaction`)
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Input refs for quick-focus pills (`+ Mã bài`, `+ Khổ in`, `+ Ups`, `+ Tờ in`)
  const codeInputRef = useRef<HTMLInputElement>(null);
  const sheetSizeInputRef = useRef<HTMLInputElement>(null);
  const sheetsInputWrapRef = useRef<HTMLDivElement>(null);
  const firstUpsWrapRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedKeyRef = useRef<string>("");

  // Notify parent container when submitting state changes
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  // Hydrate form from existingLayout (Edit) or initialDetails (Create)
  useEffect(() => {
    if (inferredEditLayoutId) {
      if (!existingLayout) return;
      const key = `edit:${existingLayout.id}`;
      if (initializedKeyRef.current === key) return;
      initializedKeyRef.current = key;

      const layoutAny = existingLayout as any;
      const effectiveLayoutSheets = Math.max(
        1,
        Number(
          layoutAny.sheetsPerUp ||
            layoutAny.totalPrintSheets ||
            layoutAny.layoutItems?.[0]?.sheetsPerUp ||
            1,
        ),
      );

      const hydratedRows: ImpositionRowState[] = (layoutAny.layoutItems || [])
        .filter((li: any) => li?.orderDetail)
        .map((li: any) =>
          mapDetailToRow(li.orderDetail, {
            overrideUps: li.ups,
            overridePlannedQty: li.plannedQty,
            sheetsPerUp: effectiveLayoutSheets,
            excludeLayoutId: layoutAny.id,
          }),
        );

      const autoSuggested = suggestSheetsPerUp(
        hydratedRows.map((r) => ({ targetQty: r.targetQty, ups: r.ups })),
      );

      setPrepressCode(String(layoutAny.layoutCode || ""));
      setSheetSize(String(layoutAny.sheetSize || ""));
      setSheetsPerUp(effectiveLayoutSheets);
      setIsManualSheetsPerUp(effectiveLayoutSheets !== autoSuggested);
      setProofUrl(String(layoutAny.fileUrl || ""));
      setNotes(String(layoutAny.notes || ""));
      setMachineId(layoutAny.machineId || null);
      setDieCutId(layoutAny.dieCutId || null);
      setShowAdvancedTech(Boolean(layoutAny.machineId || layoutAny.dieCutId));
      setRows(hydratedRows);
      return;
    }

    const detailSignature = initialDetails.map((d) => d?.id).filter(Boolean).join(",");
    const key = `create:${detailSignature}:${initialOrderCode || ""}`;
    if (initializedKeyRef.current === key) return;
    initializedKeyRef.current = key;

    const validInitial = initialDetails.filter(Boolean);
    if (validInitial.length > 0) {
      const prelimRows = validInitial.map((d) => {
        const rem = resolveRemainingPrepressQty(d);
        const targetQty = rem > 0 ? rem : Math.max(1, Number(d?.quantity || 1));
        const ups = Math.max(1, Number(d?.product?.outs || 1));
        return { targetQty, ups };
      });
      const suggestedSheets = suggestSheetsPerUp(prelimRows);
      const mapped = validInitial.map((d) =>
        mapDetailToRow(d, {
          sheetsPerUp: suggestedSheets,
          fallbackOrderCode: initialOrderCode,
        }),
      );
      const first = validInitial[0];
      const resolvedOrdCode = resolveOrderCode(first) || initialOrderCode;
      setPrepressCode(generateSuggestedLayoutCode(resolvedOrdCode));
      setSheetSize(
        String(
          first?.prepressPaperSize ||
            first?.product?.printFormat ||
            sheetSizePresets[0]?.label ||
            "",
        ),
      );
      setSheetsPerUp(suggestedSheets);
      setIsManualSheetsPerUp(false);
      setProofUrl("");
      setNotes("");
      setMachineId(null);
      setDieCutId(null);
      setRows(mapped);
    } else {
      setPrepressCode(generateSuggestedLayoutCode(initialOrderCode));
      setSheetSize(sheetSizePresets[0]?.label || "");
      setSheetsPerUp(1);
      setIsManualSheetsPerUp(false);
      setRows([]);
    }
  }, [inferredEditLayoutId, existingLayout, initialDetails, initialOrderCode, sheetSizePresets]);

  // Apply default sheet size once presets load if empty in create mode
  useEffect(() => {
    if (!inferredEditLayoutId && !sheetSize && sheetSizePresets.length > 0) {
      setSheetSize(sheetSizePresets[0].label);
    }
  }, [inferredEditLayoutId, sheetSize, sheetSizePresets]);

  // Focus requested field when opened from quick-action pill
  useEffect(() => {
    if (!focusField) return;
    const timer = window.setTimeout(() => {
      if (focusField === "code") {
        codeInputRef.current?.focus();
        codeInputRef.current?.select();
      } else if (focusField === "sheetSize") {
        sheetSizeInputRef.current?.focus();
        sheetSizeInputRef.current?.select();
      } else if (focusField === "sheets") {
        const input = sheetsInputWrapRef.current?.querySelector("input");
        input?.focus();
        input?.select();
      } else if (focusField === "ups") {
        const input = firstUpsWrapRef.current?.querySelector("input");
        input?.focus();
        input?.select();
      }
    }, 80);
    // Người dùng đã gõ / bấm trước khi hẹn giờ chạy (máy chậm) → không giật con trỏ về ô khác,
    // tránh chữ đang gõ ở ô Khổ in ghi đè lên Mã bài bình đang được bôi đen.
    const cancel = () => window.clearTimeout(timer);
    const events = ["input", "keydown", "pointerdown"] as const;
    events.forEach((ev) => document.addEventListener(ev, cancel, { capture: true, once: true }));
    return () => {
      window.clearTimeout(timer);
      events.forEach((ev) => document.removeEventListener(ev, cancel, { capture: true }));
    };
  }, [focusField]);

  // Active 3-Condition Lock Context derived from rows[0]
  const activeLockContext = useMemo(() => {
    if (rows.length === 0) return null;
    const first = rows[0];
    const mat = extractMaterialSignature(first.rawDetail);
    const route = extractRoutingSignature(first.rawDetail);
    return {
      orderId: first.orderId,
      orderCode: first.orderCode || initialOrderCode || "—",
      customerName: first.customerName,
      material: mat,
      routing: route,
      // Quy trình các sản phẩm trong bài (khác nhau vẫn ghép được — thiết lập lại ở lệnh sản xuất)
      routingLabels: Array.from(new Set(rows.map((r) => extractRoutingSignature(r.rawDetail).label))),
    };
  }, [rows, initialOrderCode]);

  // Query pending order details for the in-form picker
  const activeOrderFilter = activeLockContext?.orderId || initialOrderId || undefined;
  const { data: candidatePoolData, isFetching: isFetchingCandidates } = useQuery({
    queryKey: [
      "prepress-picker-candidates",
      activeOrderFilter || "ALL",
      pickerSearch.trim(),
    ],
    queryFn: () =>
      trpcClient.prepress.listPendingOrderDetails.query({
        tab: "CHO_BINH",
        orderId: activeOrderFilter,
        search: pickerSearch.trim() || undefined,
        limit: 50,
      }),
  });

  const candidateItems = useMemo(() => {
    const raw = (candidatePoolData as any)?.items || [];
    const selectedSet = new Set(rows.map((r) => r.orderDetailId));
    const existingRawDetails = rows.map((r) => r.rawDetail);

    return raw
      .filter((d: any) => !selectedSet.has(String(d.id)))
      .map((d: any) => {
        const compatibility = checkImpositionCompatibility(existingRawDetails, d);
        return {
          detail: d,
          compatibility,
          remainingQty: resolveRemainingPrepressQty(d),
          material: extractMaterialSignature(d),
          routing: extractRoutingSignature(d),
        };
      });
  }, [candidatePoolData, rows]);

  // Recalculate auto sheetsPerUp and row plannedQty when rows or ups change
  const recomputeWithRows = useCallback(
    (nextRows: ImpositionRowState[], manualSheetsFlag: boolean, currentSheets: number) => {
      const nextSheets = manualSheetsFlag
        ? Math.max(1, currentSheets)
        : suggestSheetsPerUp(nextRows.map((r) => ({ targetQty: r.targetQty, ups: r.ups })));

      const syncedRows = nextRows.map((r) => ({
        ...r,
        plannedQty: r.isManualPlannedQty
          ? r.plannedQty
          : calculateRowPlannedQty(r.ups, nextSheets),
      }));

      setSheetsPerUp(nextSheets);
      setRows(syncedRows);
    },
    [],
  );

  const handleAddCandidate = (detail: any) => {
    const existingRaw = rows.map((r) => r.rawDetail);
    const check = checkImpositionCompatibility(existingRaw, detail);
    if (!check.ok) {
      toast.warning(check.reason || "Sản phẩm không đủ điều kiện ghép bài!");
      return;
    }

    const newRow = mapDetailToRow(detail, {
      sheetsPerUp,
      excludeLayoutId: inferredEditLayoutId,
      fallbackOrderCode: initialOrderCode,
    });
    const nextRows = [...rows, newRow];

    if (rows.length === 0) {
      const ordCode = resolveOrderCode(detail) || initialOrderCode;
      if (!prepressCode.trim()) {
        setPrepressCode(generateSuggestedLayoutCode(ordCode));
      }
      if (!sheetSize.trim() && detail?.product?.printFormat) {
        setSheetSize(String(detail.product.printFormat));
      }
    }

    recomputeWithRows(nextRows, isManualSheetsPerUp, sheetsPerUp);
    setShowPickerDropdown(false);
    setPickerSearch("");
  };

  const handleRemoveRow = (orderDetailId: string) => {
    const nextRows = rows.filter((r) => r.orderDetailId !== orderDetailId);
    recomputeWithRows(nextRows, isManualSheetsPerUp, sheetsPerUp);
  };

  const handleUpsChange = (orderDetailId: string, nextUpsRaw: number | undefined) => {
    if (nextUpsRaw === undefined || Number.isNaN(nextUpsRaw) || nextUpsRaw <= 0) {
      setRows((prev) =>
        prev.map((r) => (r.orderDetailId === orderDetailId ? { ...r, ups: 0 } : r)),
      );
      return;
    }
    const safeUps = Math.max(1, Math.round(Number(nextUpsRaw)));
    const nextRows = rows.map((r) =>
      r.orderDetailId === orderDetailId ? { ...r, ups: safeUps } : r,
    );
    recomputeWithRows(nextRows, isManualSheetsPerUp, Math.max(1, sheetsPerUp || 1));
  };

  const handleUpsBlur = (orderDetailId: string) => {
    const target = rows.find((r) => r.orderDetailId === orderDetailId);
    if (target && (!target.ups || target.ups < 1)) {
      handleUpsChange(orderDetailId, 1);
    }
  };

  const handleRowPlannedQtyChange = (orderDetailId: string, nextQtyRaw: number | undefined) => {
    if (nextQtyRaw === undefined || Number.isNaN(nextQtyRaw) || nextQtyRaw <= 0) {
      setRows((prev) =>
        prev.map((r) =>
          r.orderDetailId === orderDetailId
            ? { ...r, plannedQty: 0, isManualPlannedQty: true }
            : r,
        ),
      );
      return;
    }
    const safeQty = Math.max(1, Math.round(Number(nextQtyRaw)));
    setRows((prev) =>
      prev.map((r) => {
        if (r.orderDetailId !== orderDetailId) return r;
        const autoVal = calculateRowPlannedQty(Math.max(1, r.ups || 1), Math.max(1, sheetsPerUp || 1));
        return {
          ...r,
          plannedQty: safeQty,
          isManualPlannedQty: safeQty !== autoVal,
        };
      }),
    );
  };

  const handleRowPlannedQtyBlur = (orderDetailId: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.orderDetailId !== orderDetailId) return r;
        if (r.plannedQty >= 1) return r;
        const fallback = calculateRowPlannedQty(Math.max(1, r.ups || 1), Math.max(1, sheetsPerUp || 1));
        return {
          ...r,
          plannedQty: Math.max(1, fallback),
          isManualPlannedQty: false,
        };
      }),
    );
  };

  const handleResetRowPlannedQty = (orderDetailId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.orderDetailId === orderDetailId
          ? {
              ...r,
              plannedQty: calculateRowPlannedQty(Math.max(1, r.ups || 1), Math.max(1, sheetsPerUp || 1)),
              isManualPlannedQty: false,
            }
          : r,
      ),
    );
  };

  const handleMasterSheetsChange = (val: number | undefined) => {
    if (val === undefined || Number.isNaN(val) || val <= 0) {
      setIsManualSheetsPerUp(true);
      setSheetsPerUp(0);
      return;
    }
    const safeSheets = Math.max(1, Math.round(Number(val)));
    setIsManualSheetsPerUp(true);
    setSheetsPerUp(safeSheets);
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        plannedQty: r.isManualPlannedQty
          ? r.plannedQty
          : calculateRowPlannedQty(Math.max(1, r.ups || 1), safeSheets),
      })),
    );
  };

  const handleResetAutoSheets = () => {
    const suggested = suggestSheetsPerUp(
      rows.map((r) => ({ targetQty: r.targetQty, ups: r.ups })),
    );
    setIsManualSheetsPerUp(false);
    setSheetsPerUp(suggested);
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        plannedQty: calculateRowPlannedQty(r.ups, suggested),
        isManualPlannedQty: false,
      })),
    );
  };

  // Proof Image Upload (Click, Drag & Drop, Ctrl+V Paste)
  const handleUploadProofFile = useCallback(async (file: File) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const res = await uploadFile(file, { preservePng: true });
      setProofUrl(res.url);
      toast.success("Đã tải ảnh bài bình lên");
    } catch (err: any) {
      toast.error(err?.message || "Không thể tải ảnh bài bình lên");
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handlePasteProof = useCallback(
    (e: React.ClipboardEvent) => {
      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;
      for (let i = 0; i < clipboardItems.length; i += 1) {
        const item = clipboardItems[i];
        if (item.type.startsWith("image/")) {
          const blob = item.getAsFile();
          if (blob) {
            e.preventDefault();
            void handleUploadProofFile(blob);
            return;
          }
        }
      }
    },
    [handleUploadProofFile],
  );

  // Totals & Validation
  const summaryStats = useMemo(() => {
    const itemCount = rows.length;
    const totalUps = rows.reduce((s, r) => s + Math.max(1, r.ups), 0);
    const totalPlannedQty = rows.reduce((s, r) => s + Math.max(0, r.plannedQty), 0);
    const suggestedSheets = suggestSheetsPerUp(
      rows.map((r) => ({ targetQty: r.targetQty, ups: r.ups })),
    );
    return {
      itemCount,
      totalUps,
      sheetsPerUp,
      totalPlannedQty,
      suggestedSheets,
    };
  }, [rows, sheetsPerUp]);

  const validationErrors = useMemo(() => {
    const errs: { code?: string; sheetSize?: string; sheetsPerUp?: string; rows?: string } = {};
    if (!prepressCode.trim()) {
      errs.code = "Vui lòng nhập Mã bài bình.";
    }
    if (!sheetSize.trim()) {
      errs.sheetSize = "Vui lòng nhập hoặc chọn Khổ giấy in.";
    }
    if (!sheetsPerUp || sheetsPerUp < 1) {
      errs.sheetsPerUp = "Số lượng tờ in mỗi Ups phải lớn hơn hoặc bằng 1.";
    }
    if (rows.length === 0) {
      errs.rows = "Vui lòng chọn ít nhất 1 chi tiết đơn hàng vào bài bình.";
    }
    return errs;
  }, [prepressCode, sheetSize, sheetsPerUp, rows.length]);

  const showCodeError = (touched.code || submitAttempted) && Boolean(validationErrors.code);
  const showSheetSizeError =
    (touched.sheetSize || submitAttempted) && Boolean(validationErrors.sheetSize);
  const showSheetsError =
    (touched.sheetsPerUp || submitAttempted) && Boolean(validationErrors.sheetsPerUp);
  const showRowsError = submitAttempted && Boolean(validationErrors.rows);

  const executeSubmit = async (createPoAfterSave: boolean) => {
    setSubmitAttempted(true);
    if (
      validationErrors.code ||
      validationErrors.sheetSize ||
      validationErrors.sheetsPerUp ||
      validationErrors.rows
    ) {
      if (validationErrors.code) codeInputRef.current?.focus();
      else if (validationErrors.sheetSize) sheetSizeInputRef.current?.focus();
      toast.warning(
        validationErrors.code ||
          validationErrors.sheetSize ||
          validationErrors.sheetsPerUp ||
          validationErrors.rows ||
          "Vui lòng kiểm tra lại thông tin bài bình.",
      );
      return;
    }

    // Re-validate 3-condition Gang-Run on frontend before network call
    if (rows.length > 1) {
      const base = rows[0].rawDetail;
      for (let i = 1; i < rows.length; i += 1) {
        const check = checkImpositionCompatibility([base], rows[i].rawDetail);
        if (!check.ok) {
          toast.warning(check.reason || "Các sản phẩm không đủ điều kiện ghép bài!");
          return;
        }
      }
    }

    setIsSubmitting(true);
    setPendingCreatePo(createPoAfterSave);
    try {
      const cleanCode = prepressCode.trim();
      const cleanSheetSize = sheetSize.trim();
      const autoName =
        rows.length > 1
          ? `Bài ghép ${rows.length} SP (${rows.map((r) => r.productName).join(" + ")})`.slice(0, 180)
          : `Bài bình ${rows[0].productName}`.slice(0, 180);

      const res: any = await batchAssignImpositionMutate({
        printLayoutId: inferredEditLayoutId || undefined,
        prepressCode: cleanCode,
        prepressPaperSize: cleanSheetSize,
        sheetsPerUp,
        totalPrintSheets: sheetsPerUp,
        prepressPrintSheets: sheetsPerUp,
        layoutName: autoName,
        layoutImage: proofUrl || null,
        fileUrl: proofUrl || null,
        notes: notes.trim() || null,
        prepressNotes: notes.trim() || null,
        machineId: machineId || null,
        dieCutId: dieCutId || null,
        items: rows.map((r) => ({
          orderDetailId: r.orderDetailId,
          ups: Math.max(1, r.ups),
          sheetsPerUp,
          plannedQty: Math.max(1, r.plannedQty),
          printSheets: sheetsPerUp,
        })),
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["prepress-hub-list"] }),
        queryClient.invalidateQueries({ queryKey: ["prepress-hub-counts"] }),
        queryClient.invalidateQueries({ queryKey: ["print-layouts-workbench"] }),
        queryClient.invalidateQueries({ queryKey: ["print-layout-detail"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["order"] }),
      ]);

      const canonicalId = String(res?.id || res?.printLayoutId || inferredEditLayoutId || "");
      const canonicalResult: PrintLayoutFormSuccessResult = {
        id: canonicalId,
        code: String(res?.code || cleanCode),
        label: String(res?.label || `${cleanCode} (${cleanSheetSize})`),
        record: res?.record || res,
        createPo: createPoAfterSave,
      };

      toast.success(
        inferredEditLayoutId
          ? `Đã cập nhật bài bình ${cleanCode}`
          : `Đã lưu bài bình ${cleanCode}`,
      );

      await onSuccess?.(canonicalResult);

      if (createPoAfterSave && canonicalId && navigateOnCreatePo) {
        navigate({
          to: "/production-orders/create",
          search: { layoutId: canonicalId } as any,
          replace: true,
        });
      }
    } catch (err: any) {
      toast.error(err?.message || "Không thể lưu thông tin bài bình");
    } finally {
      setIsSubmitting(false);
      setPendingCreatePo(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void executeSubmit(false);
  };

  if (
    inferredEditLayoutId &&
    (isLoadingExisting || (existingLayout && !prepressCode && rows.length === 0))
  ) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500 text-sm gap-2">
        <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
        <span>Đang tải thông tin bài bình...</span>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleFormSubmit}
      onPaste={handlePasteProof}
      noValidate
      className="flex flex-col gap-4"
      data-testid="canonical-print-layout-form"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* LEFT COLUMN (5 cols): THÔNG TIN CHUNG BÀI BÌNH */}
        <div className="lg:col-span-5 rounded-xl border border-slate-200 bg-white p-4 shadow-2xs space-y-3.5">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Layers className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 leading-tight">
                  Thông tin chung bài bình
                </h3>
                <p className="text-[11px] text-slate-500">
                  Mọi thông số đều cho phép người bình file tùy chỉnh
                </p>
              </div>
            </div>
            {rows.length > 1 ? (
              <Badge className="bg-indigo-600 text-white text-[10px] font-bold">
                Bài ghép ({rows.length} SP)
              </Badge>
            ) : rows.length === 1 && rows[0].targetQty < rows[0].orderQty ? (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold">
                Tách bài in
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] font-semibold text-slate-600">
                Bài đơn
              </Badge>
            )}
          </div>

          {/* 1. Mã bài bình */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="imposition-layout-code"
                className="text-xs font-semibold text-slate-700"
              >
                Mã bài bình <span className="text-rose-500">*</span>
              </Label>
              <button
                type="button"
                onClick={() =>
                  setPrepressCode(
                    generateSuggestedLayoutCode(
                      activeLockContext?.orderCode || initialOrderCode,
                    ),
                  )
                }
                className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer"
              >
                <Sparkles className="h-3 w-3" /> Gợi ý mã
              </button>
            </div>
            <Input
              ref={codeInputRef}
              id="imposition-layout-code"
              name="prepressCode"
              value={prepressCode}
              onChange={(e) => setPrepressCode(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, code: true }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                }
              }}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-invalid={showCodeError}
              aria-describedby={showCodeError ? "imposition-layout-code-error" : undefined}
              placeholder="VD: BB-2610-001"
              className={cn(
                "h-9 font-mono text-sm font-semibold",
                showCodeError && "border-rose-400 focus-visible:ring-rose-400",
              )}
            />
            {showCodeError && (
              <p
                id="imposition-layout-code-error"
                className="text-[11px] font-medium text-rose-600 flex items-center gap-1"
              >
                <AlertCircle className="h-3 w-3 shrink-0" />
                {validationErrors.code}
              </p>
            )}
          </div>

          {/* 2. Khổ giấy in */}
          <div className="space-y-1.5">
            <Label
              htmlFor="imposition-sheet-size"
              className="text-xs font-semibold text-slate-700"
            >
              Khổ giấy in <span className="text-rose-500">*</span>
            </Label>
            <Input
              ref={sheetSizeInputRef}
              id="imposition-sheet-size"
              name="prepressPaperSize"
              value={sheetSize}
              onChange={(e) => setSheetSize(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, sheetSize: true }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                }
              }}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-invalid={showSheetSizeError}
              aria-describedby={showSheetSizeError ? "imposition-sheet-size-error" : undefined}
              placeholder="Nhập tự do (VD: 65 x 86 cm hoặc 650 x 860 mm)"
              className={cn(
                "h-9 text-sm font-medium",
                showSheetSizeError && "border-rose-400 focus-visible:ring-rose-400",
              )}
            />
            {sheetSizePresets.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-0.5">
                {sheetSizePresets.map((opt) => {
                  const active = sheetSize.trim() === opt.label.trim();
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setSheetSize(opt.label);
                        setTouched((p) => ({ ...p, sheetSize: true }));
                      }}
                      className={cn(
                        "px-2 py-0.5 rounded-md text-[11px] font-mono border transition-colors cursor-pointer",
                        active
                          ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                          : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-indigo-50 hover:border-indigo-200",
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
            {showSheetSizeError && (
              <p
                id="imposition-sheet-size-error"
                className="text-[11px] font-medium text-rose-600 flex items-center gap-1"
              >
                <AlertCircle className="h-3 w-3 shrink-0" />
                {validationErrors.sheetSize}
              </p>
            )}
          </div>

          {/* 3. Số lượng tờ in mỗi Ups */}
          <div className="space-y-1.5" ref={sheetsInputWrapRef}>
            <div className="flex items-center justify-between">
              <Label
                htmlFor="imposition-sheets-per-up"
                className="text-xs font-semibold text-slate-700"
              >
                Số lượng tờ in mỗi Ups <span className="text-rose-500">*</span>
              </Label>
              {isManualSheetsPerUp ? (
                <button
                  type="button"
                  onClick={handleResetAutoSheets}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-800 cursor-pointer"
                  title="Tính lại theo công thức chuẩn ceil(SL cần SX / Số Ups)"
                >
                  <RotateCcw className="h-3 w-3" /> Tự tính ({formatNumber(summaryStats.suggestedSheets)} tờ)
                </button>
              ) : (
                <span className="text-[11px] text-emerald-700 font-medium">
                  Tự gợi ý theo Ups (sửa tự do)
                </span>
              )}
            </div>
            <NumericInput
              id="imposition-sheets-per-up"
              value={sheetsPerUp > 0 ? sheetsPerUp : ""}
              allowNegative={false}
              decimalScale={0}
              onValueChange={(values, sourceInfo) => {
                if (sourceInfo.source !== "event") return;
                handleMasterSheetsChange(values.floatValue);
              }}
              onBlur={() => setTouched((p) => ({ ...p, sheetsPerUp: true }))}
              className={cn(
                "h-9 font-mono font-bold text-indigo-700",
                showSheetsError && "border-rose-400",
              )}
              data-testid="imposition-sheets-per-up-input"
            />
            {showSheetsError && (
              <p className="text-[11px] font-medium text-rose-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3 shrink-0" />
                {validationErrors.sheetsPerUp}
              </p>
            )}
          </div>

          {/* 4. Ảnh bài bình */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-slate-700">
                Ảnh bài bình (Sơ đồ bình file)
              </Label>
              {proofUrl && (
                <button
                  type="button"
                  onClick={() => setProofUrl("")}
                  className="text-[11px] text-rose-600 hover:underline inline-flex items-center gap-0.5 cursor-pointer"
                >
                  <X className="h-3 w-3" /> Xóa ảnh
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="imposition-proof-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUploadProofFile(file);
                e.currentTarget.value = "";
              }}
            />

            {proofUrl ? (
              <div className="relative group rounded-lg border border-slate-200 bg-slate-50 overflow-hidden h-36 flex items-center justify-center">
                <img
                  src={getFullUrl(proofUrl)}
                  alt="Ảnh bài bình"
                  className="max-h-full max-w-full object-contain"
                />
                <div className="absolute inset-0 bg-slate-900/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7 text-xs"
                    onClick={() => setPreviewOpen(true)}
                  >
                    <Maximize2 className="h-3.5 w-3.5 mr-1" /> Phóng to
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7 text-xs"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-3.5 w-3.5 mr-1" /> Đổi ảnh
                  </Button>
                </div>
              </div>
            ) : (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) void handleUploadProofFile(file);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "rounded-lg border-2 border-dashed p-3.5 text-center transition-colors cursor-pointer",
                  isDragOver
                    ? "border-indigo-500 bg-indigo-50/70"
                    : "border-slate-200 bg-slate-50/60 hover:border-indigo-300 hover:bg-indigo-50/30",
                )}
              >
                {isUploading ? (
                  <div className="flex items-center justify-center gap-2 py-2 text-xs text-indigo-600 font-medium">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Đang tải ảnh bài bình...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 py-1">
                    <ImageIcon className="h-5 w-5 text-slate-400" />
                    <span className="text-xs font-medium text-slate-700">
                      Kéo thả, nhấn để chọn ảnh hoặc dán <kbd className="px-1 py-0.5 text-[10px] bg-white border rounded">Ctrl+V</kbd>
                    </span>
                    <span className="text-[10px] text-slate-400">
                      Hỗ trợ PNG, JPG, WebP rõ nét cho xưởng in đối chiếu
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 5. Ghi chú bình bài */}
          <div className="space-y-1">
            <Label htmlFor="imposition-notes" className="text-xs font-semibold text-slate-700">
              Ghi chú bài bình
            </Label>
            <Textarea
              id="imposition-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Ghi chú trở nhíp, tay kê, bù hao giấy..."
              className="text-xs resize-none"
            />
          </div>

          {/* 6. Tùy chọn Khuôn bế & Máy in */}
          <div className="border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => setShowAdvancedTech((v) => !v)}
              className="w-full flex items-center justify-between text-xs font-semibold text-slate-600 hover:text-slate-900 py-1 cursor-pointer"
            >
              <span>Khuôn bế & Máy in dự kiến (Tùy chọn)</span>
              {showAdvancedTech ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
            {showAdvancedTech && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                <div className="space-y-1">
                  <Label className="text-[11px] font-medium text-slate-600">Máy in</Label>
                  <SharedCombobox
                    type="machine"
                    value={machineId || ""}
                    onChange={(val) => setMachineId(val || null)}
                    placeholder="Chọn máy in..."
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] font-medium text-slate-600">Khuôn bế</Label>
                  <SharedCombobox
                    type="dieCut"
                    value={dieCutId || ""}
                    onChange={(val) => setDieCutId(val || null)}
                    placeholder="Chọn khuôn bế..."
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN (7 cols): THÔNG TIN CHI TIẾT ĐƠN HÀNG TRONG BÀI */}
        <div className="lg:col-span-7 rounded-xl border border-slate-200 bg-white p-4 shadow-2xs flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
            <div>
              <h3 className="text-sm font-bold text-slate-900 leading-tight">
                Chi tiết đơn hàng trong bài ({rows.length})
              </h3>
              <p className="text-[11px] text-slate-500">
                Ghép bài cho sản phẩm cùng đơn hàng, cùng chất liệu giấy; quy trình, bù hao, số ups, số lượng thiết lập lại được ở lệnh sản xuất
              </p>
            </div>
          </div>

          {/* 3-Condition Active Lock Banner */}
          {activeLockContext && (
            <div
              className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
              data-testid="gang-run-lock-banner"
            >
              <span className="inline-flex items-center gap-1 font-bold text-indigo-900">
                <ShieldCheck className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                Khóa điều kiện ghép:
              </span>
              <Badge variant="outline" className="bg-white border-indigo-200 text-indigo-800 font-mono text-[11px]">
                ĐH: {activeLockContext.orderCode}
              </Badge>
              <Badge variant="outline" className="bg-white border-emerald-200 text-emerald-800 text-[11px]">
                Giấy: {activeLockContext.material.label}
              </Badge>
            </div>
          )}

          {/* Khác quy trình: không chặn ghép — nhắc thiết lập quy trình của lệnh ở bước Lệnh sản xuất */}
          {activeLockContext && activeLockContext.routingLabels.length > 1 && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900"
              data-testid="imposition-routing-differs"
            >
              Các sản phẩm trong bài có quy trình khác nhau ({activeLockContext.routingLabels.join(" | ")}). Quy trình của
              lệnh lấy hợp các công đoạn; chỉnh lại công đoạn, bù hao ở tab Kế hoạch sản xuất khi lập lệnh.
            </div>
          )}

          {/* Picker to Add Compatible OrderDetails */}
          <div className="relative">
            <div className="relative flex items-center">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 pointer-events-none" />
              <Input
                value={pickerSearch}
                onFocus={() => setShowPickerDropdown(true)}
                onChange={(e) => {
                  setPickerSearch(e.target.value);
                  setShowPickerDropdown(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowPickerDropdown(false);
                  if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                  }
                }}
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder={
                  activeLockContext
                    ? `Thêm sản phẩm khác thuộc đơn ${activeLockContext.orderCode} vào ghép bài...`
                    : "Tìm theo mã đơn hàng, tên khách hàng hoặc tên sản phẩm chờ bình..."
                }
                className="h-9 pl-8 pr-8 text-xs"
                data-testid="imposition-detail-picker-input"
              />
              {showPickerDropdown && (
                <button
                  type="button"
                  onClick={() => setShowPickerDropdown(false)}
                  className="absolute right-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                  aria-label="Đóng danh sách chọn"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {showPickerDropdown && (
              <div
                className="mt-1 rounded-lg border border-slate-200 bg-white shadow-lg max-h-60 overflow-y-auto z-30 relative divide-y divide-slate-100"
                data-testid="imposition-detail-picker-dropdown"
              >
                {isFetchingCandidates ? (
                  <div className="p-3 text-xs text-slate-500 flex items-center gap-2">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin text-indigo-600" />
                    <span>Đang tìm chi tiết đơn hàng chờ bình...</span>
                  </div>
                ) : candidateItems.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">
                    {activeLockContext
                      ? `Không còn sản phẩm chờ bình nào khác trong đơn ${activeLockContext.orderCode}.`
                      : "Không tìm thấy chi tiết đơn hàng chờ bình phù hợp."}
                  </div>
                ) : (
                  candidateItems.map(({ detail, compatibility, remainingQty, material, routing }: any) => {
                    const ordCode = resolveOrderCode(detail);
                    return (
                      <div
                        key={detail.id}
                        className={cn(
                          "px-3 py-2 flex items-center justify-between gap-2 text-xs",
                          compatibility.ok
                            ? "hover:bg-indigo-50/50 cursor-pointer"
                            : "bg-slate-50/80 opacity-75 cursor-not-allowed",
                        )}
                        onClick={() => {
                          if (compatibility.ok) handleAddCandidate(detail);
                          else toast.warning(compatibility.reason || "Không đủ điều kiện ghép bài");
                        }}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono font-bold text-[11px] text-indigo-700">
                              {ordCode}
                            </span>
                            <span className="font-semibold text-slate-900 truncate">
                              {detail.productName || detail.product?.productName}
                            </span>
                            <Badge variant="outline" className="text-[10px] py-0 h-4">
                              Còn chờ: {formatNumber(remainingQty || detail.quantity)}
                            </Badge>
                          </div>
                          <div className="text-[11px] text-slate-500 truncate mt-0.5">
                            {material.label} • {routing.label}
                          </div>
                          {!compatibility.ok && compatibility.reason && (
                            <div className="text-[10px] font-medium text-rose-600 mt-0.5">
                              {compatibility.reason}
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant={compatibility.ok ? "outline" : "ghost"}
                          disabled={!compatibility.ok}
                          className="h-7 px-2 text-xs shrink-0"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Thêm
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Selected OrderDetails Table */}
          {rows.length > 1 && !validateBulkImpositionSelection(rows.map((r) => r.rawDetail)).ok && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 font-medium flex items-center gap-1.5"
              data-testid="imposition-incompatibility-banner"
            >
              <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
              <span>{validateBulkImpositionSelection(rows.map((r) => r.rawDetail)).reason}</span>
            </div>
          )}

          {showRowsError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 font-medium flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{validationErrors.rows}</span>
            </div>
          )}

          <div
            role="region"
            aria-label="Danh sách sản phẩm trong bài bình"
            tabIndex={0}
            className="rounded-lg border border-slate-200 overflow-x-auto"
          >
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase">
                  <th className="text-left py-2.5 px-3 min-w-[200px]">Sản phẩm & Quy cách</th>
                  <th className="text-right py-2.5 px-2.5 w-[110px]">SL Cần Bình</th>
                  <th className="text-center py-2.5 px-2.5 w-[132px]">Số Ups</th>
                  <th className="text-right py-2.5 px-2.5 w-[150px]">SL SX từng loại</th>
                  <th className="text-center py-2.5 px-2 w-[44px]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-400 text-xs">
                      Chưa có chi tiết đơn hàng nào. Hãy tìm và chọn ở ô phía trên để bắt đầu bình file.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const mat = extractMaterialSignature(row.rawDetail);
                    const delta = row.plannedQty - row.targetQty;
                    return (
                      <tr
                        key={row.orderDetailId}
                        className="bg-white hover:bg-slate-50/70 transition-colors"
                        data-testid={`imposition-row-${row.orderDetailId}`}
                      >
                        <td className="py-2.5 px-3 align-middle">
                          <div className="font-semibold text-slate-900 leading-snug">
                            {row.productName}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
                            <span className="font-mono text-indigo-600 font-semibold">
                              {row.orderCode}
                            </span>
                            {row.dimensionsLabel && <span>• {row.dimensionsLabel}</span>}
                            <span>• {mat.label}</span>
                          </div>
                        </td>

                        <td className="py-2.5 px-2.5 text-right align-middle font-mono">
                          <div className="font-bold text-slate-800">
                            {formatNumber(row.targetQty)}
                          </div>
                          {row.targetQty < row.orderQty && (
                            <div className="text-[10px] text-amber-700 font-sans">
                              / Đặt {formatNumber(row.orderQty)}
                            </div>
                          )}
                        </td>

                        <td
                          className="py-2.5 px-2.5 align-middle"
                          ref={idx === 0 ? firstUpsWrapRef : undefined}
                        >
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleUpsChange(row.orderDetailId, Math.max(1, row.ups - 1))}
                              className="h-7 w-6 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-600 cursor-pointer"
                              aria-label="Giảm số Ups"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <NumericInput
                              value={row.ups > 0 ? row.ups : ""}
                              allowNegative={false}
                              decimalScale={0}
                              onValueChange={(values, sourceInfo) => {
                                if (sourceInfo.source !== "event") return;
                                handleUpsChange(row.orderDetailId, values.floatValue);
                              }}
                              onBlur={() => handleUpsBlur(row.orderDetailId)}
                              className="h-7 w-14 text-center font-mono font-bold text-xs px-1"
                              data-testid={`imposition-ups-${row.orderDetailId}`}
                            />
                            <button
                              type="button"
                              onClick={() => handleUpsChange(row.orderDetailId, Math.max(1, row.ups) + 1)}
                              className="h-7 w-6 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-600 cursor-pointer"
                              aria-label="Tăng số Ups"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        </td>

                        <td className="py-2.5 px-2.5 text-right align-middle">
                          <div className="flex items-center justify-end gap-1">
                            <NumericInput
                              value={row.plannedQty > 0 ? row.plannedQty : ""}
                              allowNegative={false}
                              decimalScale={0}
                              onValueChange={(values, sourceInfo) => {
                                if (sourceInfo.source !== "event") return;
                                handleRowPlannedQtyChange(row.orderDetailId, values.floatValue);
                              }}
                              onBlur={() => handleRowPlannedQtyBlur(row.orderDetailId)}
                              className={cn(
                                "h-7 w-28 text-right font-mono font-bold text-xs px-2",
                                row.isManualPlannedQty
                                  ? "border-amber-300 bg-amber-50/50 text-amber-900"
                                  : "text-emerald-700",
                              )}
                              data-testid={`imposition-planned-qty-${row.orderDetailId}`}
                            />
                            {row.isManualPlannedQty && (
                              <button
                                type="button"
                                onClick={() => handleResetRowPlannedQty(row.orderDetailId)}
                                className="h-6 w-6 rounded text-amber-700 hover:bg-amber-100 flex items-center justify-center shrink-0 cursor-pointer"
                                title="Khôi phục SL SX = Số Ups × SL tờ in"
                              >
                                <RotateCcw className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <div className="text-[10px] mt-0.5 font-sans">
                            {delta === 0 ? (
                              <span className="text-slate-400">Khớp SL cần bình</span>
                            ) : delta > 0 ? (
                              <span className="text-emerald-600 font-medium">
                                +{formatNumber(delta)} dư ghép/bù
                              </span>
                            ) : (
                              <span className="text-amber-700 font-medium">
                                Tách bài (còn {formatNumber(Math.abs(delta))} chờ bình)
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-2.5 px-2 text-center align-middle">
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(row.orderDetailId)}
                            className="h-7 w-7 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center cursor-pointer"
                            title="Bỏ sản phẩm khỏi bài bình"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* 4-Metric Inline Summary Ribbon */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase text-slate-500">Số SP trong bài</div>
              <div className="text-sm font-bold font-mono text-slate-900 mt-0.5">
                {summaryStats.itemCount} <span className="text-[11px] font-sans font-normal text-slate-500">SP</span>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase text-slate-500">Tổng Số Ups</div>
              <div className="text-sm font-bold font-mono text-indigo-700 mt-0.5">
                {formatNumber(summaryStats.totalUps)} <span className="text-[11px] font-sans font-normal text-slate-500">ups/tờ</span>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase text-slate-500">SL Tờ in / Ups</div>
              <div className="text-sm font-bold font-mono text-indigo-700 mt-0.5">
                {formatNumber(summaryStats.sheetsPerUp)} <span className="text-[11px] font-sans font-normal text-slate-500">tờ</span>
              </div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase text-emerald-700">Tổng SL SX</div>
              <div className="text-sm font-bold font-mono text-emerald-800 mt-0.5">
                {formatNumber(summaryStats.totalPlannedQty)} <span className="text-[11px] font-sans font-normal text-emerald-700">SP</span>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSubmitting}
                onClick={onCancel}
                className="h-9 px-4 text-xs"
              >
                Hủy bỏ
              </Button>
            )}

            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="h-9 px-4 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              data-testid="imposition-save-btn"
            >
              {isSubmitting && !pendingCreatePo ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              {inferredEditLayoutId ? "Cập nhật Bài Bình" : "Lưu Bài Bình"}
            </Button>

            {!isDrawerMode || !inferredEditLayoutId ? (
              <Button
                type="button"
                size="sm"
                disabled={isSubmitting}
                onClick={() => void executeSubmit(true)}
                className="h-9 px-4 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                data-testid="imposition-save-and-create-po-btn"
              >
                {isSubmitting && pendingCreatePo ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Boxes className="h-3.5 w-3.5 mr-1.5" />
                )}
                {createPoLabel}
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Fullscreen Proof Image Zoom Overlay (No nested Dialog inside Form) */}
      {previewOpen && proofUrl && (
        <div
          className="fixed inset-0 z-[120] bg-slate-950/80 flex items-center justify-center p-4"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] bg-white rounded-xl p-2 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewOpen(false)}
              className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-lg cursor-pointer"
              aria-label="Đóng xem ảnh"
            >
              <X className="h-4 w-4" />
            </button>
            <img
              src={getFullUrl(proofUrl)}
              alt="Sơ đồ bài bình phóng to"
              className="max-h-[85vh] max-w-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </form>
  );
}

export default PrintLayoutForm;
